/**
 * Date Formatter - Sync moment.js locale with vue-i18n
 *
 * This utility ensures moment() always uses the current i18n locale
 * for date formatting. When user changes language, dates update automatically.
 */

import moment from "moment-timezone";
import "moment/locale/es";
import "moment/locale/en-gb";
import "moment/locale/pt-br";

/**
 * Map from i18n locale codes to moment locale codes.
 * Only needed for languages where the moment locale differs from our i18n code.
 * es → "es" (identical, no mapping needed)
 * pt-br → "pt-br" (identical, no mapping needed)
 * en → "en-gb" (British English for date formatting)
 */
const localeMap = {
  en: "en-gb",
};

/**
 * Vue plugin to sync moment locale with i18n
 * Install this plugin after creating the Vue instance
 */
export const MomentSyncPlugin = {
  install(Vue, { i18n }) {
    // Set initial locale
    setMomentLocale(i18n.locale);

    // Watch i18n locale changes globally
    Vue.prototype.$watch = Vue.prototype.$watch || function () {};
  },
};

/**
 * Setup moment sync with i18n inside a Vue component context
 * Call this in a mixin or component beforeCreate hook
 *
 * @param {object} i18n - Vue-i18n instance
 */
export function setupMomentSync(i18n) {
  // Set initial locale
  setMomentLocale(i18n.locale);

  // Return a mixin that watches locale changes
  return {
    beforeCreate() {
      // Guard: $i18n is not yet injected in some early lifecycle hooks (e.g. Root)
      if (!this.$i18n) return;
      // Use Vue's reactive system to watch i18n locale
      this.$watch(
        () => this.$i18n.locale,
        (newLocale) => {
          setMomentLocale(newLocale);
        }
      );
    },
  };
}

/**
 * Set moment locale based on i18n locale code
 *
 * @param {string} i18nLocale - Locale code from i18n (es, en, pt-br)
 */
function setMomentLocale(i18nLocale) {
  const momentLocale = localeMap[i18nLocale] || i18nLocale;
  moment.locale(momentLocale);
}

/**
 * Format date respecting current i18n locale
 *
 * @param {Date|string|moment} date - Date to format
 * @param {string} format - Moment format string (e.g., 'DD/MM/YYYY')
 * @returns {string} - Formatted date string
 */
export function formatDate(date, format = "DD/MM/YYYY") {
  return moment(date).format(format);
}

/**
 * Format date with long month name (e.g., "29 de enero 2026")
 *
 * @param {Date|string|moment} date - Date to format
 * @returns {string} - Formatted date string with locale month
 */
export function formatDateWithMonth(date) {
  const locale = moment.locale();
  if (locale === "en-gb" || locale === "en") {
    return moment(date).format("MMM DD, YYYY");
  }
  return moment(date).format("DD MMM YYYY");
}

/**
 * Format time (e.g., "14:30")
 *
 * @param {Date|string|moment} date - Date to format
 * @returns {string} - Formatted time string
 */
export function formatTime(date) {
  return moment(date).format("HH:mm");
}

/**
 * Get array of weekday names for current locale
 * Useful for calendar headers
 *
 * @returns {array} - Array of 7 weekday names (Mon-Sun)
 */
export function getWeekdayNames() {
  return [
    moment().day(1).format("ddd"), // Monday
    moment().day(2).format("ddd"), // Tuesday
    moment().day(3).format("ddd"), // Wednesday
    moment().day(4).format("ddd"), // Thursday
    moment().day(5).format("ddd"), // Friday
    moment().day(6).format("ddd"), // Saturday
    moment().day(0).format("ddd"), // Sunday
  ];
}

/**
 * Get array of month names for current locale
 *
 * @returns {array} - Array of 12 month names
 */
export function getMonthNames() {
  const months = [];
  for (let i = 0; i < 12; i++) {
    months.push(moment().month(i).format("MMMM"));
  }
  return months;
}

export default {
  setupMomentSync,
  formatDate,
  formatDateWithMonth,
  formatTime,
  getWeekdayNames,
  getMonthNames,
};
